import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import useUserContext from 'ecommerce/context/user';
import uniqBy from 'utils/uniqBy';
import useEditorItemContext from './editorItemContext';
import ancestryApi from '../api/ancestry';
import { usePollingStore } from '../lib/pollingStore';

const AncestryFamilyTreeContext = createContext();

export const AncestryFamilyTreeContextProvider = props => {
  const userContext = useUserContext();

  const { item } = useEditorItemContext();
  const { status, data } = usePollingStore();

  const [treePersons, setTreePersons] = useState();

  const personIds = useMemo(() => {
    const value = {};
    item.pages.forEach(page => {
      (page.tags ?? []).forEach(personId => {
        value[personId] = 1;
      });
    });
    return value;
  }, [item.pages]);

  const personIdsToFetch = useMemo(() => {
    const ids = { ...personIds };

    (treePersons ?? []).forEach(person => {
      delete ids[person.id];
    });

    return JSON.stringify(Object.keys(ids));
  }, [personIds, treePersons]);

  const noAncestry = useMemo(() => {

    const treeId = item.additionalInfo?.treeId;
    if (treeId != null) {
      return false;
    }
    const source = item.additionalInfo?.source;
    if (source === "ancestry") {
      return false;
    }
    return true;
  }, [item.additionalInfo]);

  useEffect(() => {
    if (noAncestry) {
      setTreePersons([]);
      return
    }
    (async (accessToken, ancestryAuth, ids) => {
      if (ids.length > 0) {
        const api = ancestryApi(accessToken, ancestryAuth);
        const personIds = ids.slice(0, 100);

        // Run both API calls in parallel
        const [personsResult, citationsResult] = await Promise.all([
          api.getPersonsInDraft(personIds),
          api.checkPersonCitations(personIds)
        ]);

        if (personsResult.data) {
          // Create set of person IDs that have citations for fast lookup
          const citationIds = new Set(citationsResult.data || []);

          // Enrich persons with citation flags
          const enrichedPersons = personsResult.data.map(person => ({
            ...person,
            hasCitations: citationIds.has(person.id)
          }));

          setTreePersons(value => uniqBy([...(value ?? []), ...enrichedPersons], 'id'));
        } else {
          setTreePersons(value => value ?? []);
          console.warn(`Error with getting persons, ids: [${personIdsToFetch}]`);
        }
      }
    })(userContext.accessToken, userContext.ancestryAuth, JSON.parse(personIdsToFetch));
  }, [noAncestry, personIdsToFetch]);

  // Clear treePersons when polling completes successfully to trigger refetch
  useEffect(() => {
    if (status === 'success' && data) {
      setTreePersons(undefined);
    }
  }, [status, data]);

  const projectPersons = useMemo(() => {
    if (Object.keys(personIds).length === 0) {
      return [];
    }

    if (!treePersons) {
      return undefined;
    }

    return treePersons.filter(person => !!personIds[person.id] && person.invalid !== true);
  }, [treePersons, personIds]);

  return (
    <AncestryFamilyTreeContext.Provider
      value={{
        treeId: item.additionalInfo?.treeId,
        projectPersons,
        noAncestry,
      }}
    >
      {props.children}
    </AncestryFamilyTreeContext.Provider>
  );
};

AncestryFamilyTreeContextProvider.propTypes = {
  children: PropTypes.node,
};

const useAncestryFamilyTreeContext = () => {
  return useContext(AncestryFamilyTreeContext);
};

export default useAncestryFamilyTreeContext;
